import { z } from 'zod';
import { daysBetween, parseBusinessDate } from '../time/index.ts';
import { businessDateSchema, uuidSchema } from './common.ts';

/** The longest period a request may span; bounds the cost of a query (docs/SECURITY.md §7). */
export const MAX_PERIOD_DAYS = 400;

export const COMPARISONS = ['previous_period', 'previous_week', 'previous_year', 'none'] as const;
export const GRANULARITIES = ['day', 'week', 'month'] as const;

/** A repeatable parameter (`?sourceId=a&sourceId=b`): one value or several. */
const idList = z
  .union([uuidSchema, z.array(uuidSchema).max(50)])
  .transform((value) => (Array.isArray(value) ? value : [value]));

const dateRange = {
  from: businessDateSchema,
  to: businessDateSchema,
};

function checkRange(value: { from: string; to: string }, ctx: z.RefinementCtx): void {
  try {
    const from = parseBusinessDate(value.from);
    const to = parseBusinessDate(value.to);
    const days = daysBetween(from, to) + 1;
    if (days < 1) ctx.addIssue({ code: 'custom', path: ['to'], message: 'must be >= from' });
    else if (days > MAX_PERIOD_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['to'],
        message: `period must not exceed ${MAX_PERIOD_DAYS} days`,
      });
    }
  } catch {
    ctx.addIssue({ code: 'custom', path: ['from'], message: 'must be a real calendar date' });
  }
}

const filters = {
  salespersonId: idList.optional(),
  sourceId: idList.optional(),
  pipelineId: idList.optional(),
};

/** Query of period-based analytics endpoints. Unknown parameters are rejected. */
export const periodQuerySchema = z
  .strictObject({
    ...dateRange,
    compare: z.enum(COMPARISONS).default('previous_period'),
    ...filters,
  })
  .superRefine(checkRange);
export type PeriodQuery = z.output<typeof periodQuerySchema>;

export const trendQuerySchema = z
  .strictObject({
    ...dateRange,
    granularity: z.enum(GRANULARITIES).optional(),
    metrics: z
      .string()
      .regex(/^[A-Za-z]+(,[A-Za-z]+)*$/, 'must be a comma-separated list of metric names')
      .optional(),
    ...filters,
  })
  .superRefine(checkRange);

export const funnelQuerySchema = z
  .strictObject({
    ...dateRange,
    mode: z.enum(['cohort', 'activity']).default('cohort'),
    ...filters,
  })
  .superRefine(checkRange);

export const lossesQuerySchema = z
  .strictObject({
    ...dateRange,
    basis: z.enum(['lost_at', 'cohort']).default('lost_at'),
    ...filters,
  })
  .superRefine(checkRange);

export const marketingQuerySchema = z
  .strictObject({
    ...dateRange,
    cohortFrom: businessDateSchema.optional(),
    cohortTo: businessDateSchema.optional(),
    ...filters,
  })
  .superRefine(checkRange)
  .superRefine((value, ctx) => {
    if ((value.cohortFrom === undefined) !== (value.cohortTo === undefined)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cohortTo'],
        message: 'cohortFrom and cohortTo must be given together',
      });
    } else if (value.cohortFrom !== undefined && value.cohortTo !== undefined) {
      checkRange({ from: value.cohortFrom, to: value.cohortTo }, ctx);
    }
  });

export const marketingTrendQuerySchema = z
  .strictObject({
    ...dateRange,
    granularity: z.enum(GRANULARITIES).optional(),
    ...filters,
  })
  .superRefine(checkRange);

export const idParamSchema = z.strictObject({ id: uuidSchema });

/** Data quality is counted for a period and may be narrowed by the usual filters. */
export const dataQualityQuerySchema = z
  .strictObject({
    ...dateRange,
    ...filters,
  })
  .superRefine(checkRange);

/** One salesperson's page: the person comes from the path, so no filters are accepted. */
export const salespersonQuerySchema = z.strictObject({ ...dateRange }).superRefine(checkRange);

/** Endpoints without parameters reject any query string. */
export const noQuerySchema = z.strictObject({});

export const adsQuerySchema = z
  .strictObject({
    ...dateRange,
    compare: z.enum(COMPARISONS).default('previous_period'),
    level: z.enum(['ad', 'ad_set', 'campaign']).default('campaign'),
    ...filters,
  })
  .superRefine(checkRange);

/** Page size limits of the client list; the analytics query enforces the same maximum. */
export const CLIENTS_MAX_LIMIT = 200;
export const CLIENTS_DEFAULT_LIMIT = 50;

/** Client list: a period, the usual filters, a name search and offset paging. */
export const clientsQuerySchema = z
  .strictObject({
    ...dateRange,
    ...filters,
    search: z.string().trim().min(1).max(100).optional(),
    limit: z.coerce.number().int().min(1).max(CLIENTS_MAX_LIMIT).default(CLIENTS_DEFAULT_LIMIT),
    offset: z.coerce.number().int().min(0).default(0),
  })
  .superRefine(checkRange);

/** A period and the usual filters, without a comparison (call patterns). */
export const periodFiltersQuerySchema = z
  .strictObject({ ...dateRange, ...filters })
  .superRefine(checkRange);
